import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaSyncAlt } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import "../assets/css/MyDay.css";

const STAT_KINDS = [
  { kind: "task", label: "Tasks", countKey: "tasks" },
  { kind: "post", label: "DM Posts", countKey: "posts" },
  { kind: "followup", label: "Follow-ups", countKey: "followups" },
];

function toISODateLocal(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("progress")) return "myday-pill--progress";
  if (s.includes("wait") || s.includes("review") || s.includes("due")) return "myday-pill--wait";
  if (s.includes("block") || s.includes("overdue") || s.includes("no response")) return "myday-pill--blocked";
  if (s.includes("done") || s.includes("posted") || s.includes("approved")) return "myday-pill--done";
  return "myday-pill--todo";
}

function MyDay({ hideStats = false }) {
  const navigate = useNavigate();
  const [date, setDate] = useState(toISODateLocal());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingKey, setUpdatingKey] = useState("");
  const [postStatuses, setPostStatuses] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [kindFilter, setKindFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [dayRes, postStatusRes, taskStatusRes] = await Promise.all([
        axiosInstance.get("/my-day", { params: { date } }),
        axiosInstance.get("/dm-post-statuses"),
        axiosInstance.get("/task-statuses"),
      ]);
      setData(dayRes.data || null);
      setPostStatuses(Array.isArray(postStatusRes.data) ? postStatusRes.data : []);
      setTaskStatuses(Array.isArray(taskStatusRes.data) ? taskStatusRes.data : []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || "Failed to load My Day");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    load();
  }, [load]);

  const items = useMemo(() => {
    if (!data) return [];
    const tasks = (data.tasks || []).map((t) => ({ ...t, _kind: "task" }));
    const posts = (data.posts || []).map((p) => ({ ...p, _kind: "post" }));
    const followups = (data.followups || []).map((f) => ({ ...f, _kind: "followup" }));
    return [...tasks, ...posts, ...followups].sort((a, b) => {
      if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
      return String(a.due_date || "").localeCompare(String(b.due_date || ""));
    });
  }, [data]);

  const updatePostStatus = async (postId, statusId) => {
    setUpdatingKey(`post-${postId}`);
    try {
      const payload = { Status_Id: Number(statusId) };
      const status = postStatuses.find((s) => String(s.Status_Id) === String(statusId));
      if (status && String(status.Status).toLowerCase() === "posted") {
        const url = window.prompt("Paste the live post URL (optional):") || "";
        if (url) payload.Posted_URL = url;
      }
      if (status && String(status.Status).toLowerCase() === "skipped") {
        const reason = window.prompt("Why is this post skipped?");
        if (!reason) return;
        payload.Skip_Reason = reason;
      }
      await axiosInstance.put(`/dm-posts/${postId}`, payload);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update post status");
    } finally {
      setUpdatingKey("");
    }
  };

  const updateTaskStatus = async (taskId, statusId) => {
    setUpdatingKey(`task-${taskId}`);
    try {
      await axiosInstance.put(`/update-task/${taskId}`, { Status_Id: Number(statusId) });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update task status");
    } finally {
      setUpdatingKey("");
    }
  };

  const updateFollowupStatus = async (item, status) => {
    setUpdatingKey(item.key);
    try {
      if (item.subtype !== "business" && item.subtype !== "client") {
        alert("Mark this follow-up from the lead page for this lead type.");
        if (item.path) navigate(item.path);
        return;
      }
      let outcome = item.Outcome || "";
      if (status === "Done" || status === "No Response") {
        outcome = window.prompt("Short outcome / note (optional):", outcome || "") || outcome;
      }
      let nextDate = null;
      if (status === "Rescheduled") {
        nextDate = window.prompt("Next follow-up date (YYYY-MM-DD):");
        if (!nextDate) return;
        nextDate = `${nextDate}T10:00:00`;
      }
      await axiosInstance.put(`/update-followup/${item.id}`, {
        Status: status,
        Outcome: outcome || null,
        ...(nextDate ? { Next_Followup_Date: nextDate } : {}),
      });
      await load();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update follow-up");
    } finally {
      setUpdatingKey("");
    }
  };

  const visibleItems = kindFilter ? items.filter((item) => item._kind === kindFilter) : items;

  return (
    <div className="myday-page">
      <div className="myday-toolbar">
        <h2 className="myday-title">My Day</h2>
        <div className="myday-toolbar__right">
          <input
            type="date"
            className="myday-date-input"
            aria-label="Date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button
            type="button"
            className="myday-btn myday-btn--icon"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
            title="Refresh"
          >
            <FaSyncAlt className={loading ? "myday-spin" : undefined} aria-hidden="true" />
            <span className="myday-btn__label">Refresh</span>
          </button>
        </div>
      </div>

      {!hideStats ? (
        <div className="myday-stats" role="group" aria-label="Filter by type">
          {STAT_KINDS.map(({ kind, label, countKey }) => (
            <button
              key={kind}
              type="button"
              className={`myday-stat myday-stat--${kind}${kindFilter === kind ? " is-active" : ""}`}
              aria-pressed={kindFilter === kind}
              onClick={() => setKindFilter((prev) => (prev === kind ? "" : kind))}
            >
              <span className="myday-stat__n">{data?.counts?.[countKey] ?? 0}</span>
              <span className="myday-stat__l">{label}</span>
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div className="myday-empty">Loading…</div>
      ) : error ? (
        <div className="myday-empty myday-empty--error">{error}</div>
      ) : visibleItems.length === 0 ? (
        <div className="myday-empty">Nothing due for this day. Nice work.</div>
      ) : (
        <div className="myday-list">
          {visibleItems.map((item) => (
            <div key={item.key} className={`myday-card${item.overdue ? " myday-card--overdue" : ""}`}>
              <div className="myday-card__main">
                <div className="myday-card__meta">
                  <span className="myday-type">{item.type}</span>
                  {item.overdue ? <span className="myday-overdue">Overdue</span> : null}
                  {item.Client_Name ? <span className="myday-client">{item.Client_Name}</span> : null}
                  {item.Platform_Name ? <span className="myday-client">{item.Platform_Name}</span> : null}
                </div>
                <button
                  type="button"
                  className="myday-card__title"
                  onClick={() => item.path && navigate(item.path)}
                >
                  {item.title}
                </button>
                <div className="myday-card__due">Due {item.due_date || "—"}</div>
              </div>
              <div className="myday-card__actions">
                {item._kind === "post" ? (
                  <select
                    className={`myday-select ${statusClass(item.status)}`}
                    aria-label="Post status"
                    disabled={updatingKey === item.key}
                    value={item.Status_Id || ""}
                    onChange={(e) => updatePostStatus(item.id, e.target.value)}
                  >
                    <option value="" disabled>Status</option>
                    {postStatuses.map((s) => (
                      <option key={s.Status_Id} value={s.Status_Id}>{s.Status}</option>
                    ))}
                  </select>
                ) : null}
                {item._kind === "task" ? (
                  <select
                    className={`myday-select ${statusClass(item.status)}`}
                    aria-label="Task status"
                    disabled={updatingKey === item.key}
                    value={item.Status_Id || ""}
                    onChange={(e) => updateTaskStatus(item.id, e.target.value)}
                  >
                    <option value="" disabled>Status</option>
                    {taskStatuses.map((s) => (
                      <option key={s.Status_Id} value={s.Status_Id}>{s.Status}</option>
                    ))}
                  </select>
                ) : null}
                {item._kind === "followup" ? (
                  <select
                    className={`myday-select ${statusClass(item.status || "Due")}`}
                    aria-label="Follow-up status"
                    disabled={updatingKey === item.key}
                    value={item.status || "Due"}
                    onChange={(e) => updateFollowupStatus(item, e.target.value)}
                  >
                    <option value="Due">Due</option>
                    <option value="Done">Done</option>
                    <option value="Rescheduled">Rescheduled</option>
                    <option value="No Response">No Response</option>
                  </select>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyDay;
